// WaveNet cloud TTS with browser fallback — fixed Mivan/Mira per language
import { API_BASE_URL } from "./constants";
import { speakSmart, resolveSpeechLanguage } from "./smartSpeech";
import { isNotificationSoundMuted } from "./alertSound";

const memCache = new Map(); // hash(text|lang|voice) -> blobUrl

function hashKey(text, speechLanguage, assistantVoice, tone) {
  return `${speechLanguage}|${assistantVoice}|${tone || 'professional'}|${text.slice(0, 1800)}`;
}

// Mirror of backend tone -> speaking rate/pitch so cloud + browser stay in sync
const TONE_VOICE_MOD = {
  friendly: { rateMul: 1.0, pitchAdd: 0.4 },
  professional: { rateMul: 1.0, pitchAdd: 0 },
  humorous: { rateMul: 1.06, pitchAdd: 1.0 },
  minimalist: { rateMul: 0.97, pitchAdd: -0.4 },
  enthusiastic: { rateMul: 1.08, pitchAdd: 1.5 },
};

function normalizeTone(tone) {
  const t = String(tone || 'professional').toLowerCase().trim();
  if (TONE_VOICE_MOD[t]) return t;
  return 'professional';
}

async function fetchWaveNet(text, opts = {}) {
  const speechLanguage = opts.speechLanguage || "en-IN";
  const assistantVoice = opts.assistantVoice || "female";
  const tone = normalizeTone(opts.tone);
  const key = hashKey(text, speechLanguage, assistantVoice, tone);
  if (memCache.has(key)) return { blobUrl: memCache.get(key), cached: true };

  const token = (() => {
    try {
      const raw = localStorage.getItem("token") || sessionStorage.getItem("token");
      return raw;
    } catch { return null; }
  })();

  const res = await fetch(`${API_BASE_URL}/tts/synthesize`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: "include",
    body: JSON.stringify({ text, speechLanguage, assistantVoice, tone }),
  });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(`TTS ${res.status}: ${body.slice(0, 200)}`);
  }
  const data = await res.json();
  const bytes = Uint8Array.from(atob(data.audioContent), (c) => c.charCodeAt(0));
  const blob = new Blob([bytes], { type: data.contentType || "audio/mpeg" });
  const blobUrl = URL.createObjectURL(blob);
  memCache.set(key, blobUrl);
  // cap cache
  if (memCache.size > 80) {
    const first = memCache.keys().next().value;
    const url = memCache.get(first);
    try { URL.revokeObjectURL(url); } catch {}
    memCache.delete(first);
  }
  return { blobUrl, cached: !!data.cached };
}

/**
 * Split a reply into speakable chunks so cloud audio plays sentence by
 * sentence with a proper gap — one giant MP3 runs everything together.
 */
function splitIntoSpokenChunks(rawText, maxChars = 550, maxChunks = 8) {
  const text = String(rawText || "").replace(/\r/g, "").trim();
  if (!text) return [];
  // Break structural lists/newlines into sentences first
  const normalized = text
    .replace(/^\s*[•·▪▫\-–—*]+\s+/gm, "")
    .replace(/^\s*\d+[.)]\s+/gm, "")
    .replace(/\n+/g, ". ");
  const sentences = normalized.split(/(?<=[.!?:])\s+/).map((s) => s.trim()).filter(Boolean);
  const chunks = [];
  let cur = "";
  const push = () => { if (cur.trim()) chunks.push(cur.trim()); cur = ""; };
  for (const sent of sentences) {
    if (sent.length > maxChars) {
      // Very long sentence: break on commas / semicolons / "and"
      const parts = sent.split(/,\s+|\s+and\s+|;\s+|\s+—\s+/);
      for (const p of parts) {
        const piece = p.trim();
        if (!piece) continue;
        const candidate = cur ? `${cur}, ${piece}` : piece;
        if (candidate.length > maxChars && cur) {
          push();
          cur = piece;
        } else {
          cur = candidate;
        }
      }
      continue;
    }
    const candidate = cur ? `${cur} ${sent}` : sent;
    if (candidate.length > maxChars && cur) {
      push();
      cur = sent;
    } else {
      cur = candidate;
    }
  }
  push();
  return chunks.slice(0, maxChunks);
}

function gapAfterChunk(chunk) {
  const t = String(chunk || "").trim();
  if (t.endsWith("?")) return 560;
  if (t.endsWith("!")) return 500;
  if (t.endsWith(":")) return 380;
  if (/[,;]$/.test(t)) return 320;
  return 430;
}

/**
 * speakWithCloud — tries WaveNet, falls back to browser smartSpeech
 * Returns controller {cancel, pause, resume, get paused, get speaking}
 * opts: {assistantVoice:'male'|'female', speechLanguage:'en-IN'|'kn-IN', onEnd,onError,onPause,onResume, preferCloud:true}
 */
export function speakWithCloud(rawText, opts = {}) {
  const preferCloud = opts.preferCloud !== false;
  const assistantVoice = opts.assistantVoice || "female";
  // Voice follows the TEXT when it clearly disagrees with the setting, so
  // older conversation messages still speak correctly after a language switch.
  // Pass strictLanguage: true to force the setting regardless of text script.
  const speechLanguage = opts.strictLanguage
    ? (opts.speechLanguage || "en-IN")
    : resolveSpeechLanguage(rawText, opts.speechLanguage || "en-IN");
  const tone = normalizeTone(opts.tone);
  const { onEnd, onError, onPause, onResume } = opts;

  let cancelled = false;
  let paused = false;
  let fallbackCtrl = null;
  let usingFallback = false;
  // Chunked cloud playback state (proper space/gap between sentences)
  let chunks = [];
  let idx = 0;
  let currentAudio = null;
  let gapTimer = null;
  let waitingGap = false;

  const clearGap = () => {
    if (gapTimer) {
      clearTimeout(gapTimer);
      gapTimer = null;
    }
  };

  const noop = () => {};
  const makeCtrl = (extra = {}) => {
    const ctrl = {
      cancel: extra.cancel || noop,
      pause: extra.pause || noop,
      resume: extra.resume || noop,
      get paused() { return paused; },
      get speaking() {
        if (cancelled) return false;
        if (usingFallback) return fallbackCtrl?.speaking ?? false;
        return idx < chunks.length || waitingGap || (currentAudio ? !currentAudio.ended : false);
      },
    };
    const fn = () => ctrl.cancel();
    Object.assign(fn, ctrl);
    fn.cancel = ctrl.cancel; fn.pause = ctrl.pause; fn.resume = ctrl.resume;
    return fn;
  };

  let controller = makeCtrl();

  // Global mute ("Mute all sounds") silences ALL project audio, including
  // assistant voice — no cloud fetch, no Audio element, no speechSynthesis.
  if (isNotificationSoundMuted()) {
    cancelled = true;
    try { if ("speechSynthesis" in window) window.speechSynthesis.cancel(); } catch {}
    return controller;
  }

  const fallbackToBrowser = () => {
    if (cancelled) return;
    clearGap();
    waitingGap = false;
    if (currentAudio) { try { currentAudio.pause(); } catch {} }
    currentAudio = null;
    usingFallback = true;
    fallbackCtrl = speakSmart(rawText, {
      assistantVoice, speechLanguage, tone,
      onEnd: () => { if (!cancelled) onEnd && onEnd(); },
      onError: (e) => { if (!cancelled) onError ? onError(e) : onEnd && onEnd(); },
      onPause, onResume,
    });
    // proxy
    controller.cancel = () => {
      cancelled = true; paused = false;
      clearGap(); waitingGap = false;
      try { fallbackCtrl.cancel(); } catch {}
    };
    controller.pause = () => {
      if (cancelled || paused) return;
      paused = true; try { fallbackCtrl.pause(); } catch {}; onPause && onPause();
    };
    controller.resume = () => {
      if (cancelled || !paused) return;
      paused = false; try { fallbackCtrl.resume(); } catch {}; onResume && onResume();
    };
    Object.defineProperty(controller, "paused", { get: () => paused });
  };

  if (!preferCloud) {
    fallbackToBrowser();
    return controller;
  }

  // Try cloud — sentence by sentence with natural gaps
  const playChunk = async (i) => {
    if (cancelled || paused) return;
    if (i >= chunks.length) {
      currentAudio = null;
      paused = false;
      onEnd && onEnd();
      return;
    }
    const chunkText = chunks[i];
    try {
      const { blobUrl } = await fetchWaveNet(chunkText, { speechLanguage, assistantVoice, tone });
      if (cancelled || paused) return;
      const audio = new Audio(blobUrl);
      currentAudio = audio;
      audio.onended = () => {
        if (cancelled || paused) return;
        idx = i + 1;
        currentAudio = null;
        // Proper gap before the next sentence so replies breathe
        waitingGap = true;
        gapTimer = setTimeout(() => {
          waitingGap = false;
          gapTimer = null;
          playChunk(idx);
        }, gapAfterChunk(chunkText));
      };
      audio.onerror = () => {
        if (cancelled || paused) return;
        if (i === 0) {
          // Nothing played yet — fall back to browser for the whole reply
          currentAudio = null;
          fallbackToBrowser();
        } else {
          // Mid-reply decode issue — skip this sentence, keep the flow
          idx = i + 1;
          currentAudio = null;
          playChunk(idx);
        }
      };
      // Small onset pause on the very first sentence (conversational turn-taking)
      const onset = i === 0 ? 180 : 0;
      gapTimer = setTimeout(() => {
        gapTimer = null;
        if (cancelled || paused) return;
        audio.play().catch(() => {
          if (i === 0) fallbackToBrowser();
          else { idx = i + 1; currentAudio = null; playChunk(idx); }
        });
      }, onset);
    } catch (e) {
      if (cancelled || paused) return;
      if (i === 0) {
        fallbackToBrowser();
      } else {
        // Speak the remainder in the browser so a long reply never cuts off
        try {
          usingFallback = true;
          const remaining = chunks.slice(i).join(" ");
          fallbackCtrl = speakSmart(remaining, {
            assistantVoice, speechLanguage, tone,
            onEnd: () => { if (!cancelled) onEnd && onEnd(); },
            onError: (err) => { if (!cancelled) onError ? onError(err) : onEnd && onEnd(); },
            onPause, onResume,
          });
        } catch {
          onEnd && onEnd();
        }
      }
    }
  };

  const startCloud = () => {
    chunks = splitIntoSpokenChunks(rawText);
    idx = 0;
    currentAudio = null;
    if (!chunks.length) {
      onEnd && onEnd();
      return;
    }
    playChunk(0);
  };

  // Wire controller to chunked audio
  controller.cancel = () => {
    cancelled = true; paused = false;
    clearGap();
    waitingGap = false;
    if (currentAudio) try { currentAudio.pause(); } catch {}
    currentAudio = null;
    if (fallbackCtrl) try { fallbackCtrl.cancel(); } catch {};
    try { window.speechSynthesis.cancel(); } catch {}
  };
  controller.pause = () => {
    if (cancelled || paused) return;
    paused = true;
    clearGap();
    if (currentAudio) {
      try { currentAudio.pause(); } catch {}
    }
    // If we were in the gap between sentences, remember to continue there
    if (!currentAudio || currentAudio.ended) waitingGap = true;
    try { if (fallbackCtrl) fallbackCtrl.pause(); } catch {}
    onPause && onPause();
  };
  controller.resume = () => {
    if (cancelled || !paused) return;
    paused = false;
    try { if (fallbackCtrl) fallbackCtrl.resume(); } catch {}
    if (usingFallback) { onResume && onResume(); return; }
    if (waitingGap || !currentAudio || currentAudio.ended) {
      waitingGap = false;
      gapTimer = setTimeout(() => { gapTimer = null; playChunk(idx); }, 150);
    } else {
      try { currentAudio.play().catch(() => {}); } catch {}
    }
    onResume && onResume();
  };

  // Try cloud — async
  (async () => {
    if (cancelled) return;
    startCloud();
  })();

  return controller;
}

export async function isCloudTtsAvailable() {
  try {
    const token = (() => { try { return localStorage.getItem("token") || sessionStorage.getItem("token"); } catch { return null; }})();
    const res = await fetch(`${API_BASE_URL}/tts/health`, {
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      credentials: "include",
    });
    return res.ok;
  } catch { return false; }
}

export { fetchWaveNet };
export default speakWithCloud;
